import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { LogOut, Trash2, Plus, Pencil, X, Inbox, Map, Bike, CalendarDays, Star, MapPin, Route, KeyRound, PartyPopper, BadgeCheck, Hotel, Users, ImagePlus, Wallet, Palette, Clapperboard, Receipt, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BookingCalendar from "@/components/admin/BookingCalendar";
import ReviewsAdmin from "@/components/admin/ReviewsAdmin";
import ThankYouButton from "@/components/admin/ThankYouButton";
import ConfirmCardButton from "@/components/admin/ConfirmCardButton";
import ItinerariesAdmin from "@/components/admin/ItinerariesAdmin";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";
import GoogleSettings from "@/components/admin/GoogleSettings";
import AgentsAdmin from "@/components/admin/AgentsAdmin";
import BrandKit from "@/components/admin/BrandKit";
import AddonsAdmin from "@/components/admin/AddonsAdmin";
import BillsAdmin, { draftFromEnquiry } from "@/components/admin/BillsAdmin";
import AgreementsAdmin, { agreementFromEnquiry } from "@/components/admin/AgreementsAdmin";
import { photoSrc } from "@/components/Reviews";

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "bills", label: "Bills", icon: Receipt },
  { key: "agreements", label: "Agreements", icon: FileSignature },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "agents", label: "Agents & Payouts", icon: Wallet },
  { key: "brand", label: "Brand Kit", icon: Palette },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "tours", label: "Tours", icon: Map },
  { key: "plans", label: "Plans", icon: Route },
  { key: "places", label: "Places", icon: MapPin },
  { key: "festivals", label: "Festivals", icon: PartyPopper },
  { key: "google", label: "Google Badge", icon: BadgeCheck },
  { key: "hotels", label: "Hotels", icon: Hotel },
  { key: "team", label: "Team", icon: Users },
  { key: "vehicles", label: "Vehicles", icon: Bike },
  { key: "addons", label: "Add-ons", icon: Clapperboard },
];

const FESTIVAL_FIELDS = [
  { key: "name", label: "Festival name" },
  { key: "start_date", label: "Start date (YYYY-MM-DD)" },
  { key: "end_date", label: "End date (YYYY-MM-DD, blank if one day)" },
  { key: "place", label: "Where" },
  { key: "category", label: "Category", type: "select", options: ["religious", "cultural", "seasonal"] },
  { key: "description", label: "Description", type: "textarea" },
  { key: "approximate", label: "Dates approximate (shows a note)", type: "checkbox" },
  { key: "package_price", label: "Package price — from ₹ (for two)", type: "number" },
  { key: "package_nights", label: "Package nights", type: "number" },
  { key: "package_includes", label: "Package includes (comma separated)", type: "csv" },
  { key: "package_enabled", label: "Show bookable package", type: "checkbox" },
  { key: "active", label: "Active (visible on site)", type: "checkbox" },
];

const HOTEL_FIELDS = [
  { key: "name", label: "Hotel name" },
  { key: "tier", label: "Tier", type: "select", options: ["budget", "mid", "premium"] },
  { key: "area", label: "Area / address" },
  { key: "near", label: "Nearby (e.g. 200 m from beach)" },
  { key: "rating", label: "Rating (0–5)", type: "number" },
  { key: "order", label: "Display order", type: "number" },
  { key: "image", label: "Cover photo", type: "image" },
  { key: "gallery", label: "More photos (rooms, beach, entrance)", type: "gallery" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "rooms", label: "Rooms — one per line: Room type | price per night", type: "rooms" },
  { key: "advance", label: "Advance to confirm booking (₹, 0 = none)", type: "number" },
  { key: "amenities", label: "Amenities (comma separated)", type: "csv" },
  { key: "maps_url", label: "Google Maps link" },
  { key: "active", label: "Active (visible on site)", type: "checkbox" },
];

const TEAM_FIELDS = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role (Driver / Rider guide / Founder…)" },
  { key: "years", label: "Years of experience", type: "number" },
  { key: "languages", label: "Languages (e.g. Odia, Hindi, English)" },
  { key: "order", label: "Display order", type: "number" },
  { key: "photo_url", label: "Photo", type: "image" },
  { key: "bio", label: "Short bio", type: "textarea" },
  { key: "active", label: "Active (visible on site)", type: "checkbox" },
];

const PLACE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "slug", label: "URL slug (auto from name if blank)" },
  { key: "zone", label: "Zone", type: "select", options: ["puri", "around", "bhubaneswar", "odisha"] },
  { key: "tagline", label: "Tagline" },
  { key: "image", label: "Image", type: "image" },
  { key: "distance", label: "Distance from Puri" },
  { key: "timings", label: "Timings" },
  { key: "best_time", label: "Best time to visit" },
  { key: "entry_fee", label: "Entry fee" },
  { key: "ideal_duration", label: "Ideal duration" },
  { key: "km_from_puri", label: "Km from Puri (for cab estimate)", type: "number" },
  { key: "lat", label: "Latitude (Google Maps)", type: "number" },
  { key: "lng", label: "Longitude (Google Maps)", type: "number" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "history", label: "History & legend", type: "textarea" },
  { key: "highlights", label: "Highlights (comma separated)", type: "csv" },
  { key: "tips", label: "Local tips", type: "textarea" },
  { key: "active", label: "Active (visible on site)", type: "checkbox" },
];

const TOUR_FIELDS = [
  { key: "title", label: "Title" },
  { key: "slug", label: "URL slug (auto from title if blank)" },
  { key: "category", label: "Category", type: "select", options: ["local", "odisha"] },
  { key: "duration", label: "Duration" },
  { key: "price", label: "Price (e.g. ₹1,499)" },
  { key: "image", label: "Cover image", type: "image" },
  { key: "pickup", label: "Pickup point & time" },
  { key: "best_season", label: "Best season" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "features", label: "Card features (comma separated)", type: "csv" },
  { key: "highlights", label: "Highlights (comma separated)", type: "csv" },
  { key: "itinerary", label: "Itinerary — one line per step: Day | Title | Details", type: "itinerary" },
  { key: "inclusions", label: "Inclusions (comma separated)", type: "csv" },
  { key: "exclusions", label: "Exclusions (comma separated)", type: "csv" },
  { key: "pricing", label: "Group price table — one per line: Label | total price (e.g. Couple (2 persons) | 14000)", type: "pricing" },
  { key: "gallery", label: "Gallery image URLs (comma separated)", type: "csv" },
  { key: "active", label: "Active (visible on site)", type: "checkbox" },
];

const VEHICLE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "type", label: "Type", type: "select", options: ["scooter", "bike"] },
  { key: "rate_12hr", label: "Rate 12 hr (₹)", type: "number" },
  { key: "rate_24hr", label: "Rate 24 hr (₹)", type: "number" },
  { key: "image", label: "Photo", type: "image" },
  { key: "specs", label: "Specs (comma separated)", type: "csv" },
  { key: "features", label: "Features (comma separated)", type: "csv" },
  { key: "available", label: "Available (visible on site)", type: "checkbox" },
];

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

function CrudSection({ resource, fields, testPrefix }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => toast.error("Failed to load"));
  }, [resource]);
  useEffect(() => { load(); }, [load]);

  const blank = () => {
    const obj = {};
    fields.forEach((f) => {
      obj[f.key] = f.type === "checkbox" ? true : f.type === "number" ? 0 : f.type === "gallery" ? [] : f.type === "csv" || f.type === "itinerary" || f.type === "rooms" || f.type === "pricing" ? "" : f.type === "select" ? f.options[0] : "";
    });
    return obj;
  };

  const toForm = (item) => {
    const obj = { ...item };
    fields.forEach((f) => {
      if (f.type === "csv") obj[f.key] = (item[f.key] || []).join(", ");
      if (f.type === "itinerary") obj[f.key] = (item[f.key] || []).map((s) => `${s.day} | ${s.title} | ${s.details}`).join("\n");
      if (f.type === "rooms") obj[f.key] = (item[f.key] || []).map((r) => `${r.type} | ${r.price}`).join("\n");
      if (f.type === "pricing") obj[f.key] = (item[f.key] || []).map((r) => `${r.label} | ${r.price}`).join("\n");
      if (f.type === "gallery") obj[f.key] = Array.isArray(item[f.key]) ? item[f.key] : [];
    });
    return obj;
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    fields.forEach((f) => {
      if (f.type === "csv") payload[f.key] = String(payload[f.key]).split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      if (f.type === "rooms") payload[f.key] = String(payload[f.key] || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [type, price] = l.split("|").map((x) => x.trim());
        return { type: type || "Room", price: Number(String(price || "").replace(/[^0-9]/g, "")) || 0 };
      });
      if (f.type === "pricing") payload[f.key] = String(payload[f.key] || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [label, price] = l.split("|").map((x) => x.trim());
        return { label: label || "Group", price: Number(String(price || "").replace(/[^0-9]/g, "")) || 0 };
      });
      if (f.type === "itinerary") payload[f.key] = String(payload[f.key] || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => {
        const [d, t, ...rest] = l.split("|").map((x) => x.trim());
        return { day: Number(d) || i + 1, title: t || "", details: rest.join(" | ") };
      });
    });
    try {
      if (payload.id) await api.put(`/admin/${resource}/${payload.id}`, payload);
      else await api.post(`/admin/${resource}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`${testPrefix}-manager`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl font-bold text-ink capitalize">{resource} ({items.length})</h2>
        <button onClick={() => setEditing(blank())} data-testid={`${testPrefix}-add-button`}
          className="flex items-center gap-2 bg-terra text-sand text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-ink transition-colors">
          <Plus size={15} /> Add New
        </button>
      </div>

      {editing && (
        <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={save}
          className="bg-white border border-terra/30 rounded-2xl p-6 mb-6 grid sm:grid-cols-2 gap-4" data-testid={`${testPrefix}-form`}>
          {fields.map((f) => (
            <div key={f.key} className={["textarea", "csv", "itinerary", "rooms", "image", "gallery"].includes(f.type) ? "sm:col-span-2" : ""}>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-ink pt-2">
                  <input type="checkbox" checked={!!editing[f.key]}
                    onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })}
                    data-testid={`${testPrefix}-field-${f.key}`} className="accent-[#D95D39] w-4 h-4" />
                  {f.label}
                </label>
              ) : (
                <>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-1.5">{f.label}</label>
                  {f.type === "textarea" || f.type === "itinerary" || f.type === "rooms" || f.type === "pricing" ? (
                    <textarea rows={f.type === "textarea" ? 3 : 4} placeholder={f.type === "itinerary" ? "1 | Dawn darshan | 5:30 AM pickup, Mangala Aarti...\n2 | Konark | Sunrise at Chandrabhaga..." : f.type === "rooms" ? "Deluxe Room | 4500\nSuite | 7000" : ""} value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className={inputCls} data-testid={`${testPrefix}-field-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select value={editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className={inputCls} data-testid={`${testPrefix}-field-${f.key}`}>
                      {f.options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : f.type === "gallery" ? (
                    <div className="flex flex-wrap gap-2 items-center" data-testid={`${testPrefix}-gallery`}>
                      {(editing[f.key] || []).map((u, i) => (
                        <span key={u + i} className="relative group">
                          <img src={photoSrc(u)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                          <button type="button" aria-label="Remove photo" onClick={() => setEditing((ed) => ({ ...ed, [f.key]: ed[f.key].filter((_, j) => j !== i) }))} data-testid={`${testPrefix}-gallery-remove-${i}`}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ink text-sand text-[10px] grid place-items-center">×</button>
                        </span>
                      ))}
                      <label className="flex items-center gap-1.5 text-xs font-semibold border border-dashed border-ink/25 px-3 py-4 rounded-xl cursor-pointer hover:bg-sand" data-testid={`${testPrefix}-gallery-add`}>
                        <ImagePlus size={14} /> Add photos
                        <input type="file" accept="image/*" multiple className="hidden" data-testid={`${testPrefix}-gallery-input`} onChange={async (e) => {
                          const files = Array.from(e.target.files || []); if (!files.length) return;
                          for (const file of files) {
                            const fd = new FormData(); fd.append("photo", file);
                            try { const r = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setEditing((ed) => ({ ...ed, [f.key]: [...(ed[f.key] || []), r.data.url] })); }
                            catch (err) { toast.error(err?.response?.data?.detail || `Upload failed: ${file.name}`); }
                          }
                          toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
                        }} />
                      </label>
                    </div>
                  ) : f.type === "image" ? (
                    <div className="flex gap-2 items-center">
                      <input type="text" value={editing[f.key] ?? ""} placeholder="Paste image URL or upload →" onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className={inputCls} data-testid={`${testPrefix}-field-${f.key}`} />
                      <label className="shrink-0 flex items-center gap-1.5 text-xs font-semibold border border-ink/15 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-sand" data-testid={`${testPrefix}-upload-${f.key}`}>
                        <ImagePlus size={14} /> Upload
                        <input type="file" accept="image/*" className="hidden" data-testid={`${testPrefix}-upload-input-${f.key}`} onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          const fd = new FormData(); fd.append("photo", file);
                          try { const r = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setEditing((ed) => ({ ...ed, [f.key]: r.data.url })); toast.success("Photo uploaded"); }
                          catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
                        }} />
                      </label>
                      {editing[f.key] && <img src={photoSrc(editing[f.key])} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />}
                    </div>
                  ) : (
                    <input type={f.type === "number" ? "number" : "text"} step={f.type === "number" ? "any" : undefined} value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className={inputCls} data-testid={`${testPrefix}-field-${f.key}`} />
                  )}
                </>
              )}
            </div>
          ))}
          <div className="sm:col-span-2 flex gap-3">
            <button type="submit" data-testid={`${testPrefix}-save-button`}
              className="bg-ink text-sand text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-terra transition-colors">
              {editing.id ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => setEditing(null)} data-testid={`${testPrefix}-cancel-button`}
              className="flex items-center gap-1 text-sm text-ink/60 px-4 py-2.5 rounded-full border border-ink/15 hover:bg-sand">
              <X size={14} /> Cancel
            </button>
          </div>
        </motion.form>
      )}

      <div className="grid gap-3">
        {items.map((item) => (
          <div key={item.id} className="bg-white border border-ink/10 rounded-2xl p-4 flex items-center gap-4" data-testid={`${testPrefix}-row-${item.id.slice(0, 8)}`}>
            {item.image && <img src={item.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-ink truncate">{item.title || item.name}</p>
              <p className="text-xs text-ink/50 truncate">
                {item.category || item.type}
                {item.price && ` · ${item.price}`}
                {item.rate_24hr != null && ` · ₹${item.rate_12hr}/12hr · ₹${item.rate_24hr}/24hr`}
                {" · "}{(item.active ?? item.available) ? "visible" : "hidden"}
              </p>
            </div>
            <button onClick={() => setEditing(toForm(item))} data-testid={`${testPrefix}-edit-${item.id.slice(0, 8)}`}
              className="p-2.5 rounded-full text-ink/60 hover:bg-sand hover:text-terra transition-colors"><Pencil size={16} /></button>
            <button onClick={() => remove(item.id)} data-testid={`${testPrefix}-delete-${item.id.slice(0, 8)}`}
              className="p-2.5 rounded-full text-ink/60 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-ink/50 py-8 text-center">No {resource} yet.</p>}
      </div>
    </div>
  );
}

function EnquiriesSection({ onCreateBill, onCreateAgreement }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load enquiries"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const setAmount = async (id, amount) => {
    const value = Math.max(0, Number(amount) || 0);
    await api.patch(`/admin/enquiries/${id}`, { amount: value });
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, amount: value } : x)));
    toast.success("Final amount saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/enquiries/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const statuses = ["new", "contacted", "confirmed"];
  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);

  return (
    <div data-testid="enquiries-inbox">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif text-2xl font-bold text-ink">Enquiries ({items.length})</h2>
        <div className="flex gap-2" data-testid="enquiries-filter">
          {["all", ...statuses].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`enquiries-filter-${s}`}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full capitalize transition-colors ${filter === s ? "bg-ink text-sand" : "bg-white border border-ink/15 text-ink/60 hover:text-ink"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-3">
        {filtered.map((e) => (
          <div key={e.id} className="bg-white border border-ink/10 rounded-2xl p-5" data-testid={`enquiry-row-${e.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-ink">{e.name} <span className="text-ink/40 font-normal">· {e.phone}</span></p>
                <p className="text-xs text-terra font-medium mt-0.5">{e.service}{e.travel_dates && ` · ${e.travel_dates}`}{e.estimate > 0 && ` · est. ₹${Number(e.estimate).toLocaleString("en-IN")}`}</p>
                {(e.ref_code || e.customer_email) && (
                  <p className="text-[11px] text-ink/50 mt-1 flex flex-wrap gap-2">
                    {e.customer_email && <span className="bg-sand px-2 py-0.5 rounded-full" data-testid={`enquiry-customer-${e.id.slice(0, 8)}`}>Signed in · {e.customer_email}</span>}
                    {e.ref_code && <span className="bg-gold/20 text-ink px-2 py-0.5 rounded-full font-mono" data-testid={`enquiry-ref-${e.id.slice(0, 8)}`}>Referred · {e.ref_code} · 10% on confirm</span>}
                  </p>
                )}
                {e.message && <p className="text-sm text-ink/70 mt-2 leading-relaxed whitespace-pre-line">{e.message}</p>}
                <p className="text-[11px] text-ink/40 mt-2 font-mono">{new Date(e.created_at).toLocaleString("en-IN")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <ConfirmCardButton enquiry={e} onSent={(id) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, confirmation_sent: true } : x)))} />
                  <ThankYouButton enquiry={e} onSent={(id) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, reminder_sent: true } : x)))} />
                  <button onClick={() => onCreateBill(e)} data-testid={`enquiry-create-bill-${e.id.slice(0, 8)}`}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${e.bill_number ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "border-ink/15 text-ink hover:bg-ink hover:text-sand"}`}>
                    <Receipt size={13} /> {e.bill_number ? `Bill ${e.bill_number} · new bill` : "Create bill"}
                  </button>
                  {(e.vehicle_name || /rental/i.test(e.service || "")) && (
                    <button onClick={() => onCreateAgreement(e)} data-testid={`enquiry-create-agreement-${e.id.slice(0, 8)}`}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${e.agreement_number ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "border-ink/15 text-ink hover:bg-ink hover:text-sand"}`}>
                      <FileSignature size={13} /> {e.agreement_number ? `${e.agreement_number} · new agreement` : "Agreement"}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-ink/50">₹
                  <input type="number" min="0" defaultValue={e.amount || ""} placeholder={e.estimate || "final"} onBlur={(ev) => Number(ev.target.value || 0) !== Number(e.amount || 0) && setAmount(e.id, ev.target.value)}
                    className="w-20 bg-sand border border-ink/15 rounded-full px-2.5 py-1.5 text-xs focus:outline-none" title="Final billed amount (used for referral commission)" data-testid={`enquiry-amount-${e.id.slice(0, 8)}`} />
                </label>
                <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id.slice(0, 8)}`}
                  className="text-xs bg-sand border border-ink/15 rounded-full px-3 py-1.5 capitalize focus:outline-none">
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id.slice(0, 8)}`}
                  className="p-2 rounded-full text-ink/50 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-ink/50 py-8 text-center">No enquiries here yet.</p>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const [billDraft, setBillDraft] = useState(null);
  const [agreementDraft, setAgreementDraft] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("cr_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/admin/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("cr_admin_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("cr_admin_token");
    navigate("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-sand flex items-center justify-center text-ink/50 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="bg-ink text-sand sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={32} testId="admin-brand-logo" />
            <span className="font-serif font-bold">Random Rider <span className="text-gold text-xs font-mono uppercase tracking-widest ml-1">Admin</span></span>
          </div>
          <div className="flex items-center gap-4">
            {integrations && (
              <span title={integrations.sms.enabled ? `Twilio SMS on (…${integrations.sms.from})` : "Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_FROM_NUMBER in Publish → Secrets to enable SMS alerts"}
                data-testid="admin-sms-status" className={`hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${integrations.sms.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-sand/10 text-sand/50"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${integrations.sms.enabled ? "bg-emerald-400" : "bg-sand/40"}`} /> SMS {integrations.sms.enabled ? "on" : "off"}
              </span>
            )}
            <Link to="/" data-testid="admin-view-site-link" className="text-xs text-sand/60 hover:text-sand transition-colors">View site</Link>
            <button onClick={() => setPwOpen(true)} data-testid="admin-change-password-button" className="flex items-center gap-1.5 text-xs text-sand/60 hover:text-sand transition-colors"><KeyRound size={13} /> Password</button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-xs font-semibold bg-sand/10 px-3.5 py-2 rounded-full hover:bg-terra transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${tab === t.key ? "bg-ink text-sand" : "bg-white border border-ink/15 text-ink/60 hover:text-ink"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && <EnquiriesSection onCreateBill={(e) => { setBillDraft(draftFromEnquiry(e)); setTab("bills"); }} onCreateAgreement={(e) => { setAgreementDraft(agreementFromEnquiry(e)); setTab("agreements"); }} />}
        {tab === "agreements" && <AgreementsAdmin draft={agreementDraft} onDraftConsumed={() => setAgreementDraft(null)} />}
        {tab === "bills" && <BillsAdmin draft={billDraft} onDraftConsumed={() => setBillDraft(null)} />}
        {tab === "calendar" && <BookingCalendar />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "tours" && <CrudSection resource="tours" fields={TOUR_FIELDS} testPrefix="tour" />}
        {tab === "plans" && <ItinerariesAdmin />}
        {tab === "google" && <GoogleSettings />}
        {tab === "agents" && <AgentsAdmin />}
        {tab === "brand" && <BrandKit />}
        {tab === "addons" && <AddonsAdmin />}
        {tab === "hotels" && <CrudSection resource="hotels" fields={HOTEL_FIELDS} testPrefix="hotel" />}
        {tab === "team" && <CrudSection resource="team" fields={TEAM_FIELDS} testPrefix="team" />}
        {tab === "festivals" && <CrudSection resource="festivals" fields={FESTIVAL_FIELDS} testPrefix="festival" />}
        {tab === "places" && <CrudSection resource="places" fields={PLACE_FIELDS} testPrefix="place" />}
        {tab === "vehicles" && <CrudSection resource="vehicles" fields={VEHICLE_FIELDS} testPrefix="vehicle" />}
            <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </main>
    </div>
  );
}
